/* Custom CSS Variables for Brand Colors */
:root {
  /* Primary Colors */
  --color-primary-blue: #4176ba;
  --color-primary-green: #349b94;
  
  /* Accent Colors */
  --color-accent-orange: #ff914d;
  --color-accent-yellow: #ffde59;
  
  /* Support Colors */
  --color-support-light-blue: #71c8dc;
  --color-support-aqua-green: #b1dfdd;
  
  /* Neutral Colors */
  --color-neutral-light-gray: #e2e3e7;
  --color-neutral-graphite: #363640;
  --color-neutral-white: #ffffff;
  
  /* Background Colors */
  --color-bg-light: #d9d9d9;
  --color-bg-white: #ffffff;
  
  /* Typography */
  --font-headline: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  --font-body: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  
  /* Shadows */
  --shadow-sm: 0px 2px 4px rgba(54, 54, 64, 0.08);
  --shadow-md: 0px 4px 8px rgba(54, 54, 64, 0.12);
  --shadow-lg: 0px 8px 16px rgba(54, 54, 64, 0.16);
  
  /* Transitions */
  --transition-fast: 0.15s ease-in-out;
  --transition-normal: 0.2s ease-in-out;
  --transition-slow: 0.3s ease-in-out;
}

/* Reset and Base Styles */
* {
  box-sizing: border-box;
}

html {
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  scroll-behavior: smooth;
  /* Fix for iOS Safari viewport issues */
  height: -webkit-fill-available;
}

body {
  margin: 0;
  padding: 0;
  font-family: var(--font-body);
  font-weight: 400;
  line-height: 1.6;
  color: var(--color-neutral-graphite);
  background-color: var(--color-bg-white);
  /* Fix for iOS Safari 100vh issue */
  min-height: 100vh;
  min-height: -webkit-fill-available;
  /* Prevent iOS Safari bounce effect */
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  /* Safe area support for iPhone X and newer */
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
}

/* Headings */
h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-headline);
  font-weight: 800;
  color: var(--color-neutral-graphite);
  margin-top: 0;
  margin-bottom: 1rem;
  line-height: 1.2;
}

h1 {
  font-size: 3rem;
  letter-spacing: -0.02em;
}

h2 {
  font-size: 2.5rem;
  letter-spacing: -0.01em;
}

h3 {
  font-size: 2rem;
  letter-spacing: -0.01em;
}

h4 {
  font-size: 1.5rem;
}

h5 {
  font-size: 1.25rem;
}

h6 {
  font-size: 1rem;
}

/* Responsive Typography */
@media (max-width: 768px) {
  h1 {
    font-size: 2.5rem;
  }
  
  h2 {
    font-size: 2rem;
  }
  
  h3 {
    font-size: 1.75rem;
  }
  
  h4 {
    font-size: 1.25rem;
  }
}

/* Links */
a:not([data-component="footer"] a) {
  color: var(--color-primary-blue);
  text-decoration: none;
  transition: color var(--transition-fast);
}

a:not([data-component="footer"] a):hover {
  color: #2d5082; /* Darker blue for hover */
  text-decoration: underline;
}

/* Focus Styles for Accessibility */
:focus {
  outline: 2px solid var(--color-primary-blue);
  outline-offset: 2px;
}

:focus:not(:focus-visible) {
  outline: none;
}

:focus-visible {
  outline: 2px solid var(--color-primary-blue);
  outline-offset: 2px;
}

/* Selection */
::selection {
  background-color: var(--color-support-light-blue);
  color: var(--color-neutral-graphite);
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

::-webkit-scrollbar-track {
  background: var(--color-neutral-light-gray);
}

::-webkit-scrollbar-thumb {
  background: var(--color-primary-blue);
  border-radius: 6px;
  border: 2px solid var(--color-neutral-light-gray);
}

::-webkit-scrollbar-thumb:hover {
  background: #2d5082;
}

/* Utility Classes */
.text-primary {
  color: var(--color-primary-blue);
}

.text-secondary {
  color: var(--color-primary-green);
}

.bg-light {
  background-color: var(--color-bg-light);
}

.bg-white {
  background-color: var(--color-bg-white);
}

/* Performance Optimizations */
img, video {
  max-width: 100%;
  height: auto;
}

/* iPhone and iOS Safari specific fixes */
@supports (-webkit-touch-callout: none) {
  /* Fix for iOS Safari input zoom */
  input[type="text"],
  input[type="email"],
  input[type="password"],
  input[type="number"],
  input[type="tel"],
  input[type="url"],
  input[type="search"],
  textarea,
  select {
    font-size: 16px !important;
    -webkit-appearance: none;
    border-radius: 0;
  }
  
  /* Fix for iOS Safari button styling */
  button,
  input[type="submit"],
  input[type="button"] {
    -webkit-appearance: none;
    border-radius: 0;
  }
  
  /* Improve touch targets */
  button,
  a,
  input,
  select,
  textarea {
    min-height: 44px;
    min-width: 44px;
  }
  
  /* Fix tap highlight color */
  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  
  button:active,
  a:active {
    -webkit-tap-highlight-color: rgba(65, 118, 186, 0.2);
  }
  
  /* Fix text selection */
  * {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
  
  /* Allow text selection for content */
  p, span, div[contenteditable], input, textarea, h1, h2, h3, h4, h5, h6 {
    -webkit-user-select: text;
    user-select: text;
  }
  
  /* Fix sticky positioning */
  .sticky {
    position: -webkit-sticky;
    position: sticky;
  }
  
  /* Hardware acceleration for smooth animations */
  .animated {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }
}

/* Reduce motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* CRUD Management Styles */

/* Page Layout */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-neutral-light-gray);
}

.page-header h1 {
  margin: 0;
  color: var(--color-neutral-graphite);
}

/* Filters */
.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.filter-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-neutral-graphite);
  margin-bottom: 0.25rem;
}

/* Cards */
.card {
  background: var(--color-bg-white);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-neutral-light-gray);
  background: var(--color-bg-light);
}

.card-body {
  padding: 1.5rem;
}

/* Tables */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  background: var(--color-bg-light);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-neutral-graphite);
  border-bottom: 2px solid var(--color-neutral-light-gray);
  white-space: nowrap;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid var(--color-neutral-light-gray);
  vertical-align: top;
}

.table tr:hover {
  background: rgba(65, 118, 186, 0.05);
}

.table tr.selected {
  background: rgba(65, 118, 186, 0.1);
}

.table tr.deleted-row {
  opacity: 0.6;
  background: rgba(220, 53, 69, 0.05);
}

/* Sortable Table Styles */
.table th.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: background-color var(--transition-fast);
}

.table th.sortable:hover {
  background: rgba(65, 118, 186, 0.1);
}

.th-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.sort-icon {
  font-size: 0.75rem;
  opacity: 0.6;
  transition: opacity var(--transition-fast);
  min-width: 12px;
  text-align: center;
}

.table th.sortable:hover .sort-icon {
  opacity: 1;
}

.table th.sortable .sort-icon {
  color: var(--color-primary-blue);
}

/* Search Input Styles */
.search-input-container {
  position: relative;
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  border: 2px solid var(--color-neutral-light-gray);
  border-radius: 4px;
  font-size: 1rem;
  font-family: var(--font-body);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  background: var(--color-bg-white);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary-blue);
  box-shadow: 0 0 0 3px rgba(65, 118, 186, 0.1);
}

.search-input-icons {
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: none;
}

.search-clear-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  transition: all var(--transition-fast);
  pointer-events: auto;
}

.search-clear-btn:hover {
  background: var(--color-neutral-light-gray);
  color: var(--color-neutral-graphite);
}

.search-icon {
  color: #6c757d;
  font-size: 1rem;
}

.search-input:focus + .search-input-icons .search-icon {
  color: var(--color-primary-blue);
}

/* Forms */
.form {
  max-width: 100%;
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-neutral-light-gray);
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.form-section h3 {
  margin-bottom: 1rem;
  color: var(--color-neutral-graphite);
  font-size: 1.25rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  color: var(--color-neutral-graphite);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-control {
  padding: 0.75rem;
  border: 2px solid var(--color-neutral-light-gray);
  border-radius: 4px;
  font-size: 1rem;
  font-family: var(--font-body);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  background: var(--color-bg-white);
}

.form-control:focus {
  outline: none;
  border-color: var(--color-primary-blue);
  box-shadow: 0 0 0 3px rgba(65, 118, 186, 0.1);
}

.form-control.error {
  border-color: #dc3545;
}

.form-control:disabled {
  background: var(--color-bg-light);
  color: #6c757d;
  cursor: not-allowed;
}

.form-help {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.error-message {
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
  width: 1.25rem;
  height: 1.25rem;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: var(--font-body);
  text-decoration: none;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all var(--transition-fast);
  min-height: 44px;
  gap: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--color-primary-blue);
  color: var(--color-neutral-white);
  border-color: var(--color-primary-blue);
}

.btn-primary:hover:not(:disabled) {
  background: #2d5082;
  border-color: #2d5082;
}

.btn-secondary {
  background: var(--color-neutral-light-gray);
  color: var(--color-neutral-graphite);
  border-color: var(--color-neutral-light-gray);
}

.btn-secondary:hover:not(:disabled) {
  background: #d1d2d6;
  border-color: #d1d2d6;
}

.btn-danger {
  background: #dc3545;
  color: var(--color-neutral-white);
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
  border-color: #c82333;
}

.btn-warning {
  background: #ffc107;
  color: var(--color-neutral-graphite);
  border-color: #ffc107;
}

.btn-warning:hover:not(:disabled) {
  background: #e0a800;
  border-color: #e0a800;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  min-height: 36px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-neutral-light-gray);
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.badge-success {
  background: #d4edda;
  color: #155724;
}

.badge-danger {
  background: #f8d7da;
  color: #721c24;
}

.badge-warning {
  background: #fff3cd;
  color: #856404;
}

.badge-info {
  background: #d1ecf1;
  color: #0c5460;
}

/* Loading States */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-neutral-light-gray);
  border-top: 4px solid var(--color-primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Alerts */
.alert {
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid transparent;
}

.alert-error {
  background: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
}

.alert-success {
  background: #d4edda;
  color: #155724;
  border-color: #c3e6cb;
}

.alert-warning {
  background: #fff3cd;
  color: #856404;
  border-color: #ffeaa7;
}

.alert-info {
  background: #d1ecf1;
  color: #0c5460;
  border-color: #bee5eb;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item label {
  font-weight: 600;
  color: var(--color-neutral-graphite);
  font-size: 0.875rem;
}

.info-item span {
  color: #6c757d;
  font-size: 0.875rem;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--color-neutral-light-gray);
  background: var(--color-bg-light);
}

.pagination-info {
  font-size: 0.875rem;
  color: var(--color-neutral-graphite);
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .action-buttons {
    justify-content: center;
  }
  
  .table-container {
    font-size: 0.75rem;
  }
  
  .table th,
  .table td {
    padding: 0.5rem;
  }
  
  .pagination {
    flex-direction: column;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .btn {
    width: 100%;
    justify-content: center;
  }
  
  .btn-sm {
    width: auto;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .action-buttons .btn {
    width: 100%;
  }
}

/* Contract Period Selector Styles */
.contract-period-selector {
  width: 100%;
  margin: 1rem 0;
}

.period-buttons-container {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.period-button {
  flex: 1 1 150px;
  max-width: 200px;
  min-height: 80px;
  padding: 1rem;
  border: 2px solid var(--color-neutral-light-gray);
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-normal);
  position: relative;
  font-family: var(--font-body);
  color: var(--color-neutral-graphite);
}

.period-button:hover {
  border-color: var(--color-primary-blue);
  background-color: rgba(65, 118, 186, 0.05);
}

.period-button.active {
  border-color: var(--color-primary-blue);
  background-color: rgba(65, 118, 186, 0.1);
  box-shadow: var(--shadow-sm);
}

.period-text {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.savings-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: var(--color-accent-yellow);
  color: var(--color-neutral-graphite);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
}

@media (max-width: 768px) {
  .period-buttons-container {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .period-button {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }
}